/**
 * Deaf-accessible incoming-call attention for the web app.
 *
 * A ringing phone is useless to a Deaf user, and navigator.vibrate is a
 * no-op on desktop. This raises attention through channels a Deaf user can
 * actually perceive: a flashing browser-tab title (visible even when the tab
 * is in the background), a persistent Web Notification with the caller's
 * name, and — for anyone who wants it — an audible cue is intentionally left
 * to the OS notification, not forced here.
 *
 * startCallAttention returns a stop function; always call it on accept,
 * decline, or timeout so the tab title and notification are cleared.
 */
import { showDesktopWindow } from '../base/util/desktopWindow';
const FLASH_INTERVAL_MS = 900;
let activeStop = null;
function canNotify() {
    return typeof window !== 'undefined' && 'Notification' in window;
}
/**
 * Ask for notification permission ahead of time (e.g. when the user signs
 * in) so the first incoming call can show a system notification without a
 * permission prompt racing the ring.
 */
export function primeCallNotifications() {
    if (canNotify() && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => undefined);
    }
}
export function startCallAttention({ callerName, body }) {
    // Only one incoming-call attention runs at a time.
    if (activeStop) {
        activeStop();
    }
    const originalTitle = typeof document !== 'undefined' ? document.title : '';
    const flashTitle = `☎ ${callerName} calling…`;
    let showingFlash = true;
    let interval = null;
    if (typeof document !== 'undefined') {
        document.title = flashTitle;
        interval = setInterval(() => {
            document.title = showingFlash ? originalTitle : flashTitle;
            showingFlash = !showingFlash;
        }, FLASH_INTERVAL_MS);
    }
    let notification = null;
    if (canNotify() && Notification.permission === 'granted') {
        try {
            notification = new Notification(`${callerName} is calling`, {
                body: body || 'Incoming Malka call',
                tag: 'malka-incoming-call',
                requireInteraction: true
            });
            notification.onclick = () => {
                // The desktop app shows its window, also a hidden one.
                showDesktopWindow();
                try {
                    window.focus();
                }
                catch (e) { /* ignore */ }
                notification?.close();
            };
        }
        catch (e) {
            // Some browsers throw when constructing notifications off a user
            // gesture; the flashing title still covers the accessibility need.
            notification = null;
        }
    }
    const stop = () => {
        if (interval) {
            clearInterval(interval);
            interval = null;
        }
        if (typeof document !== 'undefined') {
            document.title = originalTitle;
        }
        if (notification) {
            try {
                notification.close();
            }
            catch (e) { /* ignore */ }
            notification = null;
        }
        if (activeStop === stop) {
            activeStop = null;
        }
    };
    activeStop = stop;
    return stop;
}
export function stopCallAttention() {
    if (activeStop) {
        activeStop();
    }
}
